import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import axios from 'axios';
import { AutocompleteOption, AutocompleteFieldProps  } from '../types';
import './AutocompleteField.css';


const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  label,
  value,
  onChange,
  field = 'name',
  placeholder = '',
  required = false,
  className = '',
  disabled = false,
  endpoint = '/api/search/products', // Default to the correct endpoint
  limit = 10,
  showLabel = true,
  authToken, // Destructure the authToken prop
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<AutocompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [error, setError] = useState<string | null>(null); // New state for error messages

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Update local state when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Use useCallback for fetchSuggestions to stabilize its reference
  const fetchSuggestions = React.useCallback(async (query: string) => {
    setError(null); // Clear previous errors when starting a new fetch
    
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        setIsLoading(true);

        // Add Authorization header if authToken is provided
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

        const response = await axios.get(endpoint, {
          params: { query: encodeURIComponent(query), field, limit }, // Encode the query
          headers: headers, // Pass headers
        });
        
        // Ensure we don't have duplicates by value
        const uniqueValues = new Map();
        response.data.forEach((item: AutocompleteOption) => {
          if (!uniqueValues.has(item.value)) {
            uniqueValues.set(item.value, item);
          }
        });
        
        setSuggestions(Array.from(uniqueValues.values()));
        setShowSuggestions(true);
        setHighlightedIndex(-1);
      } catch (err: any) {
        console.error('Error fetching suggestions:', err); // Log the full error object for debugging

        // Detailed error handling based on Axios response
        if (err.response) {
          console.error("API Error Response Data (422 likely):", err.response.data); // Log response data
          if (err.response.status === 422) {
            // Attempt to parse FastAPI's validation errors
            const detail = err.response.data?.detail;
            if (Array.isArray(detail) && detail.length > 0) {
              const errorMessage = detail.map((d: any) => {
                const loc = Array.isArray(d.loc) ? d.loc.join('.') : 'unknown';
                return `${loc}: ${d.msg}`;
              }).join('; ');
              setError(`Validation error: ${errorMessage}`);
            } else if (typeof detail === 'string') {
              setError(`Validation error: ${detail}`);
            } else {
              setError("Validation error occurred (check console for details).");
            }
          } else if (err.response.status === 401 || err.response.status === 403) {
              setError("Authentication required or not authorized.");
          } else {
              setError(`API Error: ${err.response.status} - ${err.response.statusText || 'Unknown'}`);
          }
        } else if (err.request) {
          setError("Network Error: No response from server. Check your connection or server status.");
        } else {
          setError("An unexpected error occurred while setting up the request.");
        }
        setSuggestions([]); // Clear suggestions on error
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, [endpoint, field, limit, authToken]); // Add authToken to useCallback dependencies

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setError(null); // Clear error immediately when user types
    onChange(newValue, undefined); // Propagate change to parent immediately
    fetchSuggestions(newValue);
  };

  // Handle selection of a suggestion
  const handleSelectSuggestion = (suggestion: AutocompleteOption) => {
    if (suggestion.value !== inputValue) {
      setInputValue(suggestion.value);
      onChange(suggestion.value, suggestion.id);
    }
    setSuggestions([]);
    setShowSuggestions(false);
    setError(null); // Clear error on successful selection
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prevIndex) => 
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setError(null); // Clear error on escape
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current && 
        suggestionsRef.current &&
        !inputRef.current.contains(event.target as Node) && 
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        // Only clear error if input is empty, otherwise keep it for user to see
        if (!inputValue) setError(null); 
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputValue]); // Added inputValue to dependencies to prevent stale closure for error clearing

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionsRef.current) {
      const highlightedElement = suggestionsRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className={`autocomplete-container ${className}`}>
      {showLabel && (
        <label htmlFor={`autocomplete-${label}`} className="autocomplete-label">
          {label} {required && <span className="autocomplete-required">*</span>}
        </label>
      )}
      <div className="autocomplete-input-wrap">
        <input
          id={`autocomplete-${label}`}
          ref={inputRef}
          type="text"
          className={`autocomplete-field${disabled ? ' is-disabled' : ''}`}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {isLoading && (
          <div className="autocomplete-spinner" aria-hidden="true" />
        )}
      </div>
      
      {error && <p className="autocomplete-error">{error}</p>} {/* Display error message */}

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="autocomplete-dropdown"
          /* The width is measured from the input at render time, so it is the
             one thing here that cannot live in the stylesheet. */
          style={{ width: inputRef.current?.offsetWidth ?? '100%' }}
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.id}-${index}`}
              /* The highlighted state was set twice: once by this class and
                 once by an inline background/colour that shadowed it. */
              className={`autocomplete-option${index === highlightedIndex ? ' selected' : ''}`}
              onClick={() => handleSelectSuggestion(suggestion)}
              role="option"
              aria-selected={index === highlightedIndex}
            >
              {suggestion.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutocompleteField;